import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession, effectiveRoleView, usePrimaryModule, type Session } from '../../lib/session';
import { useAuthStore } from '../../lib/auth';
import { isLegacyHost, SAAS_HOME_URL } from '../../lib/host';

/**
 * Separation-of-duties gate. Manager-only surfaces (4R lifecycle decisions,
 * finance posting, admin/settings) are restricted to managers; field-tier
 * operators are redirected away. Platform admins and directors ALWAYS pass —
 * they can never be locked out.
 *
 * Enforcement is deliberate and safe:
 *  - while the session is resolving (not ready), this renders nothing, so no
 *    privileged user ever flashes-blocked;
 *  - ⚠️ if resolution FAILS or stalls, the session is now the denied one (16 Sep):
 *    lowest tier, no permissions. It used to fall back to a director and pass,
 *    which meant one dropped request opened every gate on this page. The old
 *    comment here said "the fallback is still a director → passes" and described
 *    a hole as a feature; corrected when the hole was closed.
 */
export function isManager(s: Pick<Session, 'isPlatformAdmin' | 'tier'>): boolean {
  return s.isPlatformAdmin || s.tier === 'director' || s.tier === 'coordinator';
}

/**
 * Admin gate — STRICTER than isManager. Only platform admins and directors
 * (which includes super_user via tierFromSlug) may reach admin surfaces
 * (entity/user module allocation, role-permission matrix). Coordinators are
 * managers but NOT admins, so they're excluded. This is defense-in-depth: the
 * real boundary is the DB write policy (Lane A Task 15); this stops a non-admin
 * touching the write controls client-side and keeps the UX honest.
 */
export function isAdmin(s: Pick<Session, 'isPlatformAdmin' | 'tier'>): boolean {
  return s.isPlatformAdmin || s.tier === 'director';
}

/**
 * Signed-out callers get the login page, NOT a blank screen.
 *
 * Without this, a logged-out visitor hit `ready:false` forever (the session only
 * resolves once there IS an auth user — session.tsx returns early otherwise), so
 * the guard rendered `null` and every manager route served HTTP 200 with an empty
 * body. Verified in Chrome on 20 Jul: /consolidated, /ic-consolidation and
 * /money-flow were all silently blank. Mirrors the existing RequireAuth in
 * router.tsx. Safe to read here: the auth store hydrates SYNCHRONOUSLY at module
 * load precisely to avoid a redirect-to-login race, so this cannot flash for a
 * signed-in user.
 */
export function RequireManager({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const s = useSession();
  if (!user) return <Navigate to="/auth/login" replace />;   // signed out → login, never blank
  if (!s.ready) return null;              // wait for the real role before deciding
  // Gate on the EFFECTIVE (view-as-aware) role. For a real user this equals their
  // real role; when an admin is previewing a lower role it enforces that lower
  // role's access — the preview can only ever restrict, never widen (RLS is the
  // real wall regardless).
  if (isManager(effectiveRoleView(s))) return <>{children}</>;
  return <NotYourScreen />;
}

/**
 * What a refusal looks like to the person refused (Ravi, 16 Sep: "add the line").
 *
 * Until now a guarded route bounced him home in silence. On a phone that reads as a tap that
 * did not work — so he taps again, decides the app is faulty, and says so to whoever gave him
 * the phone. That is how a working guard gets reported as a broken app during a field week.
 * He gets a sentence instead: what this screen is, who holds it, and the way back to his own
 * work. No data loads behind it — the guarded children never render.
 */
function NotYourScreen() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-sm w-full rounded-[12px] border border-[#e2e8f0] bg-white p-5 text-center">
        <div className="text-[15px] font-semibold text-[#0f172a]">That screen isn’t yours</div>
        <p className="text-[13px] text-[#64748b] mt-1.5">
          The office looks after this one. If you need something off it, ask them — they can send it to you.
        </p>
        <a href="/home"
          className="inline-block mt-4 rounded-[8px] bg-[#0d9488] px-4 py-2 text-[13px] font-semibold text-white">
          Back to my day
        </a>
      </div>
    </div>
  );
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const s = useSession();
  if (!user) return <Navigate to="/auth/login" replace />;   // signed out → login, never blank
  if (!s.ready) return null;              // wait for the real role before deciding
  if (isAdmin(effectiveRoleView(s))) return <>{children}</>;
  return <Navigate to="/home" replace />;
}

/**
 * Platform-Admin gate — the STRICTEST gate in the app. Only true platform admins
 * (rainmaker_users.is_platform_admin, surfaced on the session) may pass. This is
 * deliberately stricter than isAdmin, which also admits directors: Jody is a
 * director but NOT a platform admin, so isAdmin is not good enough for the PMO
 * Director Command Centre (/cc/director), which is gated to platform admins only.
 * NOTE: this admits ANY is_platform_admin user, not Ravi specifically — so
 * "Ravi-only" holds only while Ravi is the SOLE is_platform_admin in dmhw. That
 * must be verified in the DB (codex flagged it); this guard does not assert it.
 *
 * Hydration/loading: while the session is resolving we wait (`!s.ready → null`),
 * exactly like the other guards, so a real platform admin is never flash-denied
 * during auth. Unlike RequireManager/RequireAdmin (whose fallback session is a
 * director → fail-open), the fallback session's isPlatformAdmin is false, so this
 * gate is fail-CLOSED — the correct posture for the strictest boundary. The real
 * boundary remains RLS at the DB; this keeps the surface platform-admin-only client-side.
 */
export function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const s = useSession();
  if (!user) return <Navigate to="/auth/login" replace />;   // signed out → login
  if (!s.ready) return null;              // wait for the resolved flag before deciding
  // Effective platform-admin: a preview drops it to false, so an admin previewing
  // a lower role is correctly kept out of the platform-admin-only Command Centre.
  if (s.effectiveIsPlatformAdmin) return <>{children}</>;
  // Signed-in but not a platform admin → the app home. On the legacy host the
  // home page lives on the other domain, so hard-redirect cross-domain there.
  if (isLegacyHost()) {
    window.location.href = SAAS_HOME_URL;
    return null;
  }
  return <Navigate to="/home" replace />;
}

/**
 * `/my-day` is the PROPERTY operator's home (OperatorDashboard): rent collected against target,
 * payment obligations, municipal arrears, "bill all tenants on the 1st", "capture meter readings".
 * It was built for Akhil and it is right for him.
 *
 * 🔴 It was NOT right for a salesperson, and on 23 Sep Ravi found it on Avinesh's phone on live —
 * an R83,500 rent target, R59,350 of obligations, and Jonah telling him the COMPANY's 150 overdue
 * tasks were what was holding HIS target back. The mobile Dashboard tab routed on TIER, and a
 * salesperson and a property operator are both 'field'.
 *
 * The tab is fixed; this closes the deep link, so typing or bookmarking /my-day cannot show a
 * salesperson the property books either. Everyone else is untouched.
 *
 * ⚠️ THIS IS A UI REDIRECT, NOT A CONTROL. He could still read those figures if a screen asked for
 * them: `obligations` grants SELECT to any MEMBER of the entity, and get_property_leases_self
 * checks membership without ever consulting the role. Narrowing that is Lane A's, and until it is
 * done the money is withheld by routing rather than by the database.
 */
export function PropertyOperatorHome({ children }: { children: ReactNode }) {
  const s = useSession();
  const primaryModule = usePrimaryModule();
  if (!s.ready) return null;                       // decide only on a resolved session
  if (primaryModule === 'sales') return <Navigate to="/sales-rep" replace />;
  return <>{children}</>;
}
